/* eslint-disable @homeaway/translation-rules/localesCheck */
// @flow
import React, {useCallback, useState} from 'react';
import {parseISO} from 'date-fns';
import {FormattedMessage} from 'react-intl';
import {Waypoint} from '@homeaway/react-unveil';
import {ButtonIcon} from '@homeaway/react-buttons';
import {CHEVRON_LEFT__16, CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import PropertyCarousel from '../PropertyCarousel/PropertyCarousel';
import {trigger as edapTrigger} from '../../utils/edapWrapper';
import {
    triggerPresentedSerpFlexDatesCarouselEvent,
    triggerSelectedSerpFlexDatesCarouselCardEvent,
    triggerPresentedSerpFlexDatesCarouselCardEvent
} from '../../utils/collectionEdapUtils';
import {FLEX_DATES_CAROUSEL} from '../../utils/carouselEdapUtils';
import {DESKTOP_BREAKPOINTS, MOBILE_BREAKPOINTS} from './breakpoints';

import type {PropertyCardListingShape} from '../PropertyCarousel/PropertyCard/PropertyCard';

import './FlexDatesCarousel.less';

type Props = {
    flexibleDateListings: PropertyCardListingShape[],
    isMobile: boolean,
    position: string,
    locale: string
};

const flexDatesLabelGenerator = ({listingRequestParams}, locale) => {
    const {arrival, departure} = listingRequestParams;
    const options = {month: 'short', day: 'numeric'};
    const arrivalDate = parseISO(arrival);
    const departureDate = parseISO(departure);

    return `${arrivalDate.toLocaleString(
        locale,
        options
    )} - ${departureDate.toLocaleString(locale, options)}`;
};

const getIndex = ({cardIndex, cardCount, cardsTotal, next}) => {
    const changeAmount = cardCount;
    return next
        ? Math.min(cardsTotal - cardCount, cardIndex + changeAmount)
        : Math.max(0, cardIndex - changeAmount);
};

const FlexDatesCarousel = ({
    isMobile,
    flexibleDateListings,
    position,
    locale
}: Props) => {
    const [waypointEntered, setWaypointEntered] = useState(false);
    const breakpoints = isMobile ? MOBILE_BREAKPOINTS : DESKTOP_BREAKPOINTS;
    const [cardIndex, setCardIndex] = useState(0);
    const [cardCount, setCardCount] = useState(1);
    const [cardsTotal, setCardsTotal] = useState(flexibleDateListings.length);
    const [showControls, setShowControls] = useState(false);

    const onNextClick = () => {
        setCardIndex(
            getIndex({
                cardIndex,
                cardCount,
                cardsTotal,
                next: true
            })
        );
        edapTrigger('ux.cards_visible.adjusted', {
            action_location: 'flex-dates-module',
            event_action: 'next_arrow_clicked'
        });
        edapTrigger('nav.next.click', {
            actionlocation: 'flex-dates-module'
        });
    };
    const onPreviousClick = () => {
        setCardIndex(
            getIndex({
                cardIndex,
                cardCount,
                cardsTotal,
                next: false
            })
        );
        edapTrigger('ux.cards_visible.adjusted', {
            action_location: 'flex-dates-module',
            event_action: 'previous_arrow_clicked'
        });
        edapTrigger('nav.previous.click', {
            actionlocation: 'flex-dates-module'
        });
    };

    const onCarouselResize = ({
        numberCardsVisible,
        numberCardsTotal,
        needsControls
    }) => {
        setCardCount(numberCardsVisible);
        setCardsTotal(numberCardsTotal);
        setShowControls(needsControls);
    };

    const handleWaypointEnter = useCallback(() => {
        if (!waypointEntered) {
            setWaypointEntered(true);
            triggerPresentedSerpFlexDatesCarouselEvent(position);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [position, waypointEntered]);

    return (
        <Waypoint onEnter={handleWaypointEnter} useWindowAsScrollParent>
            <div className={`FlexDatesCarousel FlexDatesCarousel--${position}`}>
                <div className="PropertyCarousel">
                    <PropertyCarousel
                        isMobile={isMobile}
                        cardIndex={cardIndex}
                        breakpoints={breakpoints}
                        listings={flexibleDateListings}
                        onCarouselResize={onCarouselResize}
                        onCardClick={
                            triggerSelectedSerpFlexDatesCarouselCardEvent
                        }
                        onCardEnter={
                            triggerPresentedSerpFlexDatesCarouselCardEvent
                        }
                        edapCollectionType={FLEX_DATES_CAROUSEL}
                        title={
                            <div className="PropertyCarousel__header">
                                <div className="PropertyCarousel__header-content">
                                    <h2>
                                        <FormattedMessage
                                            id="flexDatesCarousel.headline"
                                            description="title for flex dates carousel"
                                            defaultMessage="Flexible dates? Try these."
                                        />
                                    </h2>
                                </div>
                                {!isMobile && (
                                    <div className="PropertyCarousel__header-controls">
                                        <ButtonIcon
                                            label="Previous"
                                            svgMarkup={CHEVRON_LEFT__16}
                                            circle
                                            size="xs"
                                            onClick={onPreviousClick}
                                            disabled={cardIndex === 0}
                                        />
                                        <ButtonIcon
                                            label="Next"
                                            svgMarkup={CHEVRON_RIGHT__16}
                                            circle
                                            size="xs"
                                            onClick={onNextClick}
                                            disabled={
                                                cardIndex ===
                                                    cardsTotal - cardCount ||
                                                !showControls
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        }
                        withImagesCarousel
                        labelGenerator={flexDatesLabelGenerator}
                        locale={locale}
                    />
                </div>
            </div>
        </Waypoint>
    );
};

export default FlexDatesCarousel;
